import { comboIndex, COMBO_COUNT, parseRange, totalWeight } from '../range';
import { nearestPushFoldBand } from './staging';
/**
 * Map a full 9-max `Position` onto the push/fold chart's position bucket (PF5 routing). EP = UTG/UTG1/
 * UTG2, MP = LJ/HJ, then CO/BTN/SB verbatim - and, unlike the cash `positionBucket` (villainRange.ts,
 * which collapses BB→SB because BB never opens), **BB is preserved** here: the call-off chart authors a
 * distinct BB cell (BB is the one seat that faces a shove closing the action), so a BB call-off must not
 * lose its identity.
 */
export const pushFoldPositionOf = (pos) => {
    switch (pos) {
        case 'UTG':
        case 'UTG1':
        case 'UTG2':
            return 'EP';
        case 'LJ':
        case 'HJ':
            return 'MP';
        case 'CO':
            return 'CO';
        case 'BTN':
            return 'BTN';
        case 'SB':
            return 'SB';
        case 'BB':
            return 'BB';
    }
};
/** The shover's class from their push/fold position: a late-position (CO/BTN/SB) open-shove is a wide
 *  steal (hero calls wider); an early-position or blind shove is tight (hero calls tighter). Selects the
 *  vsSteal / vsEarly call-off range in `resolveCallOff`. */
export const shoverClassOf = (shover) => shover === 'CO' || shover === 'BTN' || shover === 'SB' ? 'steal' : 'early';
/** Combo-% of a parsed equilab string (the DT charts are unweighted, so this is the covered fraction
 *  of all 1326 combos). Matches the DT validator's `pct`. */
const rangePctOf = (range) => (totalWeight(parseRange(range)) / COMBO_COUNT) * 100;
/** Is hero's specific combo present (weight > 0) in the parsed range string? */
const comboInRange = (range, hole) => {
    const parsed = parseRange(range);
    return parsed.weights[comboIndex(hole[0], hole[1])] > 0;
};
/**
 * Open-shove decision: hero is first to act (folded to hero) at push/fold depth. Snaps the live
 * effective stack to the nearest authored band, reads the jam range for hero's position, and jams iff
 * hero's combo is in it. Throws if the chart has no jam cell for (position, band) - the DT charts cover
 * EP/MP/CO/BTN/SB at every band, so a missing cell means the caller passed a non-jamming position
 * (e.g. BB, which is never first-in preflop).
 */
export const resolveJam = (chart, position, effBB, hole) => {
    const band = nearestPushFoldBand(effBB);
    const cell = chart.jam[position]?.[band];
    if (!cell)
        throw new RangeError(`${chart.id}: no jam range for ${position} @ ${band}`);
    const inRange = comboInRange(cell.range, hole);
    return {
        action: inRange ? 'jam' : 'fold',
        inRange,
        position,
        band,
        rangePct: rangePctOf(cell.range),
    };
};
/**
 * Call-off decision: hero faces an all-in shove. Snaps to the nearest band, reads the call range for
 * hero's calling position split by the shover's class (steal vs early), and calls iff hero's combo is
 * in it. Throws if the chart has no call cell for (position, band) - the DT charts cover CO/BTN/SB/BB.
 */
export const resolveCallOff = (chart, position, effBB, shoverClass, hole) => {
    const band = nearestPushFoldBand(effBB);
    const cell = chart.callVsShove[position]?.[band];
    if (!cell)
        throw new RangeError(`${chart.id}: no call range for ${position} @ ${band}`);
    const range = shoverClass === 'steal' ? cell.vsSteal.range : cell.vsEarly.range;
    const inRange = comboInRange(range, hole);
    return {
        action: inRange ? 'call' : 'fold',
        inRange,
        position,
        band,
        shoverClass,
        rangePct: rangePctOf(range),
    };
};
//# sourceMappingURL=pushfold.js.map